import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import type { MarketClass } from 'frontend-utils/utils/constants/benchmark-insight';
import { BENI_COMPARISON_SETS } from 'frontend-utils/utils/constants/benchmark-insight';
import { convertKilometersToMiles } from 'frontend-utils/utils/distance';
import HotelBenchmarkInsightMarketCompositionMarketCompositionDetailsItem from 'lumen/components/hotel/benchmark-insight/market-composition/market-composition-details-item';
import type { BeniMarketCompositionMarket } from 'lumen/models';
import {
  BENI_COMPARISON_SET_LONG_TRANSLATIONS,
  BENI_MARKET_CLASS_TRANSLATIONS,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';

interface HotelBenchmarkInsightMarketCompositionMarketCompositionDetailsSignature {
  Args: {
    categories: {
      name: string;
      value: string;
    }[];
    market: BeniMarketCompositionMarket;
  };
}

export default class HotelBenchmarkInsightMarketCompositionMarketCompositionDetails extends Component<HotelBenchmarkInsightMarketCompositionMarketCompositionDetailsSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  get comparisonSetTypeTranslation(): string {
    const { market } = this.args;
    const comparisonSetType = market.comparisonSetType;

    if (comparisonSetType) {
      return this.intl.t(
        BENI_COMPARISON_SET_LONG_TRANSLATIONS[comparisonSetType],
      );
    }

    return '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelClassDescription() {
    if (
      this.args.market.comparisonSetType ===
      BENI_COMPARISON_SETS.MARKET_COLLAPSED_CLASS
    ) {
      return this.translatedCollapsedClasses;
    }

    if (this.translatedMarketClass) {
      return this.intl.t(this.translatedMarketClass);
    }

    return '';
  }

  get hotelClassGroupShare(): string | null {
    const { market } = this.args;

    return market.marketClassGroupShare !== null
      ? this.formatPercentage(market.marketClassGroupShare)
      : null;
  }

  get hotelIconName(): IconName {
    return 'business-type-market';
  }

  get hotelProximityRadius(): string {
    const conversion = this.formatDistance(this.args.market.proximityRadius);

    const translation = this.intl.t(
      `benchmark_insight.market_composition.radius_${
        this.hotelAndMarketService.useImperialSystem ? 'mile' : 'km'
      }`,
      { count: conversion },
    );

    return translation;
  }

  get hotelProximityShare(): string {
    const { market } = this.args;

    if (!market.proximityShare) {
      return '';
    }

    return this.formatPercentage(market.proximityShare);
  }

  get hotelsCount(): string {
    return `${this.args.market.hotelCount} ${this.intl.t('general.hotels').toLowerCase()}`;
  }

  get hotelStarsRating(): string {
    return `${this.intl.t('benchmark_insight.market_composition.stars_rated', {
      count: this.hotelAndMarketService.hotel?.hotelinfo.stars,
    })} ${this.intl.t(
      'benchmark_insight.market_composition.stars_rated.count',
      {
        count: this.hotelAndMarketService.hotel?.hotelinfo.stars,
      },
    )}`;
  }

  get isMarket(): boolean {
    return this.args.market.comparisonSetType === BENI_COMPARISON_SETS.MARKET;
  }

  get isRadius(): boolean {
    return this.args.market.comparisonSetType === BENI_COMPARISON_SETS.RADIUS;
  }

  get isSmartCompset(): boolean {
    return (
      this.args.market.comparisonSetType === BENI_COMPARISON_SETS.SMART_COMPSET
    );
  }

  get pinIconName(): IconName {
    return 'location-pin';
  }

  get roomsDetailsHeader(): string {
    const classTranslation = this.intl.t(
      BENI_COMPARISON_SET_LONG_TRANSLATIONS[this.args.market.comparisonSetType],
    );

    return this.intl.t(
      'benchmark_insight.market_composition_detail.available_rooms',
      {
        class: classTranslation,
      },
    );
  }

  get starRatingGroupShare(): string {
    const value = this.args.market.starRatingGroupShare;
    if (!value) {
      return '';
    }

    return this.formatPercentage(value);
  }

  get translatedCollapsedClasses(): string {
    const classes =
      this.hotelAndMarketService.hotel
        ?.benchmarkInsightHotelClassesInCollapsedClass;
    const translatedClasses = classes?.map((classKey) => {
      const classKeyExists = this.isMarketClass(classKey);
      if (classKeyExists) {
        return this.intl.t(BENI_MARKET_CLASS_TRANSLATIONS[classKey]);
      }
      return '';
    });

    return this.intl.t(
      'benchmark_insight.market_composition_detail.collapsed_classes',
      {
        classOne: translatedClasses?.[0],
        classTwo: translatedClasses?.[1],
      },
    );
  }

  get translatedMarketClass(): string | null {
    const marketClass: MarketClass | undefined =
      this.hotelAndMarketService.hotel?.benchmarkInsightHotelClass;

    let marketClassTranslation = null;
    if (marketClass) {
      const marketClassKeyExists = this.isMarketClass(marketClass);
      if (marketClassKeyExists) {
        marketClassTranslation = BENI_MARKET_CLASS_TRANSLATIONS[marketClass];
      }
    }
    return marketClassTranslation;
  }

  get travelDestination(): string | null {
    return (
      this.hotelAndMarketService.hotel?.benchmarkInsightTravelDestination ??
      null
    );
  }

  formatDistance(proximityRadius = 0): string {
    const kilometers = proximityRadius;
    const miles = convertKilometersToMiles(proximityRadius);
    const correctMeasurement = this.hotelAndMarketService.useImperialSystem
      ? miles
      : kilometers;
    return correctMeasurement.toFixed(1);
  }

  formatPercentage(value: number): string {
    return `${this.intl.formatNumber(value, {
      style: 'percent',
    })}`;
  }

  isMarketClass(marketClass: MarketClass): boolean {
    return marketClass !== undefined;
  }

  <template>
    <div
      class="details__container
        {{if this.isSmartCompset 'details__container--smart-compset'}}"
    >
      {{#if this.isSmartCompset}}
        <div
          class="details__container__section details__container__section--smart-compset flex flex-col gap-300"
        >
          <div class="details__container__section__title">
            <p class="text-300--regular text-neutral-default">{{t
                "benchmark_insight.market_composition.smart_compset.considered_categories"
              }}</p>
          </div>
          <div
            class="details__container__section__body details__container__section__body--smart-compset flex flex-row gap-200"
          >
            {{#each @categories as |category|}}
              <HotelBenchmarkInsightMarketCompositionMarketCompositionDetailsItem
                @subTitle={{category.value}}
                @title={{category.name}}
              />
            {{/each}}
          </div>
        </div>
      {{/if}}

      {{#unless this.isSmartCompset}}
        <div class="details__container__section flex flex-col gap-300">
          <p class="text-300--regular text-neutral-default">{{t
              "benchmark_insight.market_composition.details"
            }}</p>

          <div class="details__container__section__body">
            {{#unless (or this.isMarket this.isRadius)}}
              <HotelBenchmarkInsightMarketCompositionMarketCompositionDetailsItem
                @fillRow={{true}}
                @iconName={{this.hotelIconName}}
                @subTitle={{this.comparisonSetTypeTranslation}}
                @title={{this.hotelClassDescription}}
              />
            {{/unless}}

            <HotelBenchmarkInsightMarketCompositionMarketCompositionDetailsItem
              @iconName={{this.hotelIconName}}
              @subTitle={{t
                "benchmark_insight.market_composition_detail.included_in"
              }}
              @title={{this.hotelsCount}}
            />

            {{#if this.travelDestination}}
              <HotelBenchmarkInsightMarketCompositionMarketCompositionDetailsItem
                @dataTestId="market-composition-details-travel-destination"
                @iconName={{this.pinIconName}}
                @subTitle={{t
                  "benchmark_insight.market_composition_detail.market-area"
                }}
                @title={{this.travelDestination}}
              />
            {{/if}}
          </div>
        </div>
      {{/unless}}

      <div class="details__container__section flex flex-col gap-300">
        <div class="details__container__section__title flex flex-col gap-100">
          <p class="text-300--regular text-neutral-default">{{t
              "benchmark_insight.market_composition_detail.rooms"
            }}</p>

          <p class="text-200--regular text-neutral-subdued">
            {{#if this.isSmartCompset}}
              {{t
                "benchmark_insight.market_composition.smart_compset.hotel_details"
              }}
            {{else}}
              {{this.roomsDetailsHeader}}
            {{/if}}
          </p>
        </div>

        <div class="details__container__section__body">
          {{#if this.isSmartCompset}}
            {{#if this.travelDestination}}
              <HotelBenchmarkInsightMarketCompositionMarketCompositionDetailsItem
                @dataTestId="market-composition-details-travel-destination"
                @fillRow={{true}}
                @iconName={{this.pinIconName}}
                @subTitle={{t
                  "benchmark_insight.market_composition_detail.market-area"
                }}
                @title={{this.travelDestination}}
              />
            {{/if}}
          {{/if}}

          {{#if this.isSmartCompset}}
            <div class="details__container__section__body--details-container">
              {{#if this.hotelClassGroupShare}}
                <HotelBenchmarkInsightMarketCompositionMarketCompositionDetailsItem
                  @subTitle={{this.hotelClassDescription}}
                  @title={{this.hotelClassGroupShare}}
                />
              {{/if}}

              <HotelBenchmarkInsightMarketCompositionMarketCompositionDetailsItem
                @subTitle={{this.hotelProximityRadius}}
                @title={{this.hotelProximityShare}}
              />

              <HotelBenchmarkInsightMarketCompositionMarketCompositionDetailsItem
                @subTitle={{this.hotelStarsRating}}
                @title={{this.starRatingGroupShare}}
              />
            </div>
          {{else}}
            {{#if this.hotelClassGroupShare}}
              <HotelBenchmarkInsightMarketCompositionMarketCompositionDetailsItem
                @subTitle={{this.hotelClassDescription}}
                @title={{this.hotelClassGroupShare}}
              />
            {{/if}}

            <HotelBenchmarkInsightMarketCompositionMarketCompositionDetailsItem
              @subTitle={{this.hotelProximityRadius}}
              @title={{this.hotelProximityShare}}
            />

            <HotelBenchmarkInsightMarketCompositionMarketCompositionDetailsItem
              @subTitle={{this.hotelStarsRating}}
              @title={{this.starRatingGroupShare}}
            />
          {{/if}}
        </div>
      </div>
    </div>
  </template>
}
